import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAPI } from "../../api/apiHelper";
import Toast from "../../components/Toast";
import useToast from "../../hooks/useToast";
import { NavLink } from "react-router-dom";
import { Logo } from "../../components/logo/Logo";
import styles from "./Auth.module.css";
import dec from "../../assets/landing/dec.svg";
import { Mail, RefreshCw } from "lucide-react";
import "./Login.css";

export const VerifyEmailPending = () => {
  const [isResending, setIsResending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { toast, success, error, hideToast } = useToast();

  // Get user email from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userEmail = userData.email || "";

  const handleResendEmail = async () => {
    if (!userEmail) {
      error("Email not found. Please sign up again.", {
        title: "Error",
      });
      return;
    }

    setIsResending(true);
    try {
      await authAPI.resendVerification(userEmail);
      setEmailSent(true);
      success("Verification email sent! Please check your inbox.", {
        title: "Email Sent",
      });
    } catch (err) {
      console.error("Resend error:", err);
      
      if (err.message?.includes("already verified")) {
        error("Your email is already verified. You can log in now!", {
          title: "Already Verified",
        });
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.message?.includes("not found")) {
        error("User not found. Please sign up again.", {
          title: "User Not Found",
        });
      } else {
        error(err.message || "Failed to resend email. Please try again.", {
          title: "Resend Failed",
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/login");
  };

  return (
    <div className="login-root">
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        title={toast.title}
        duration={toast.duration}
        onClose={hideToast}
        position={toast.position}
      />
      <div className="login-left-panel">
        <NavLink to="/">
          <Logo
            variant="white authLogo"
            className={styles.logo}
          />
        </NavLink>
        <img src={dec} alt="Decorative background shape" className="svg_style" />
        <img src={dec} alt="Decorative background shape" className="svg_style_1" />
        <div className="login-features">
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Secure Account</div>
              <div className="login-feature-desc">
                Email verification ensures your account security and authenticity
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Quick Setup</div>
              <div className="login-feature-desc">
                Just one click to verify and you're ready to go
              </div>
            </div>
          </div>
          <div className="login-feature">
            <span className="login-check">✔</span>
            <div>
              <div className="login-feature-title">Instant Access</div>
              <div className="login-feature-desc">
                Get immediate access to all features after verification
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="login-right-panel">
        <div className="login-form" style={{ maxWidth: "500px" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                margin: "0 auto 20px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mail size={40} color="white" />
            </div>
            <div className="login-title">Verify Your Email</div>
            <div className="login-subtitle" style={{ marginTop: "16px" }}>
              We've sent a verification link to
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1976d2",
                marginTop: "8px",
              }}
            >
              {userEmail}
            </div>
          </div>

          <div
            style={{
              background: "#f0f7ff",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #bedeff",
            }}
          >
            <div style={{ fontSize: "14px", lineHeight: "1.6", color: "#333" }}>
              <p style={{ margin: "0 0 12px 0" }}>
                📧 <strong>Check your inbox</strong> for an email from us
              </p>
              <p style={{ margin: "0 0 12px 0" }}>
                🔗 Click the <strong>verification link</strong> in the email
              </p>
              <p style={{ margin: "0" }}>
                ✅ You'll be redirected to login automatically
              </p>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <p style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
              ✅ Already verified your email?
            </p>
            <button
              onClick={handleGoToLogin}
              style={{
                background: "#1976d2",
                color: "white",
                border: "none",
                padding: "12px 30px",
                borderRadius: "6px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#1565c0";
                e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#1976d2";
                e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
              }}
            >
              Go to Login →
            </button>
          </div>

          <div
            style={{
              marginTop: "32px",
              padding: "16px",
              background: "#fff3cd",
              borderRadius: "6px",
              border: "1px solid #ffc107",
              fontSize: "13px",
              color: "#856404",
            }}
          >
            <strong>💡 Tips:</strong>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
              <li>The verification link opens in a new tab - check all browser tabs</li>
              <li>Check spam folder if you don't see the email</li>
              <li>This page can be closed after you click the verification link</li>
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .spin-animation {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default VerifyEmailPending;
